'use client';

import { c as _c } from "react/compiler-runtime";
import { LinkComponent } from '@/components/ui/link-component';
import { HeadlineH1 } from '@/components/ui/typography/headline-h1';
import { ParagraphText } from '@/components/ui/typography/paragraph-text';
import { TeaserText } from '@/components/ui/typography/teaser-text';
import type { Locale } from '@/types/types';
import { i18nConfig } from '@/types/types';
import { useCurrentLocale } from 'next-i18n-router/client';
import { useParams, useSearchParams } from 'next/navigation';
import type React from 'react';
/**
 * Lightweight JWT decode function for client-side use.
 * Only decodes the payload - does NOT verify signatures.
 * This avoids bundling the heavy crypto-browserify (~320KB) dependency.
 */
function decodeJwtPayload<T>(token: string): T | undefined {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return undefined;
    const payload = parts[1] as string;
    // Base64Url decode
    const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');
    const jsonPayload = decodeURIComponent([...atob(base64)]
    // eslint-disable-next-line unicorn/prefer-code-point
    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload) as T;
  } catch {
    return undefined;
  }
}
interface StaticTranslationString {
  en: string;
  de: string;
  fr: string;
}
const previewTokenInvalidText: StaticTranslationString = {
  en: 'The preview token is invalid.',
  de: 'Das Vorschau-Token ist ungültig.',
  fr: "Le jeton d'aperçu n'est pas valide."
};
const urlDoesNotMatchText: StaticTranslationString = {
  en: 'URL does not match, you are on',
  de: 'URL stimmt nicht überein, du bist auf',
  fr: "l'URL ne correspond pas, vous êtes sur"
};
const previewForUrlText: StaticTranslationString = {
  en: 'Preview for URL:',
  de: 'Vorschau für URL:',
  fr: "Aperçu pour l'URL :"
};
const issuedAtText: StaticTranslationString = {
  en: 'Issued At:',
  de: 'Ausgestellt am:',
  fr: 'Délivré le :'
};
const documentIdText: StaticTranslationString = {
  en: 'Document ID:',
  de: 'Dokumenten-ID:',
  fr: 'ID du document :'
};
const expiresAtText: StaticTranslationString = {
  en: 'Expires At:',
  de: 'Ablaufdatum:',
  fr: 'Expire le :'
};
const expiredParenthesisText: StaticTranslationString = {
  en: ' (expired)',
  de: ' (abgelaufen)',
  fr: ' (expiré)'
};
const notAvailableText: StaticTranslationString = {
  en: 'N/A',
  de: 'Nicht verfügbar',
  fr: 'Non disponible'
};
const error403TitleText: StaticTranslationString = {
  en: '403 - Preview is Not Available',
  de: '403 - Vorschau nicht verfügbar',
  fr: '403 - Aperçu non disponible'
};
const requestingPreviewModeText: StaticTranslationString = {
  en: "You are requesting a page in preview mode! However, the page is not available. This might be due to the fact that the page's URL has changed or the preview token has expired.",
  de: 'Du forderst eine Seite im Vorschaumodus an! Die Seite ist jedoch nicht verfügbar. Dies könnte daran liegen, dass sich die URL der Seite geändert hat oder das Vorschau-Token abgelaufen ist.',
  fr: "Vous demandez une page en mode aperçu! Cependant, la page n'est pas disponible. Cela peut être dû au fait que l'URL de la page a changé ou que le jeton d'aperçu a expiré."
};
const pageNotAvailableP1Text: StaticTranslationString = {
  en: 'Go back to the ',
  de: 'Gehe zurück zur ',
  fr: 'Retournez à la '
};
const homePageLinkText: StaticTranslationString = {
  en: 'home page',
  de: 'Startseite',
  fr: "page d'accueil"
};
const pageNotAvailableP2Text: StaticTranslationString = {
  en: ' or request a new preview token.',
  de: ' oder fordere ein neues Vorschau-Token an.',
  fr: " ou demandez un nouveau jeton d'aperçu."
};
const backToHomeHandler = (): void => {
  globalThis.location.href = '/';
};
const dateStringFormat: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
};
interface DecodedToken {
  iat: number | string;
  exp: number | string;
  url?: string;
  id?: string;
}
interface PreviewTokenAnalysisProperties {
  validPreviewToken: boolean;
  previewTokenExpired: boolean;
  decoded: DecodedToken | undefined;
}
const PreviewTokenAnalysis: React.FC<PreviewTokenAnalysisProperties> = t0 => {
  const $ = _c(15);
  const {
    validPreviewToken,
    previewTokenExpired,
    decoded
  } = t0;
  const locale = useCurrentLocale(i18nConfig) as Locale;
  const {
    slugs
  } = useParams();
  const path = globalThis.location.pathname;
  let iatDate;
  let t1;
  if ($[0] !== decoded || $[1] !== locale) {
    const currentLocaleForDate = {
      en: "en-US",
      de: "de-DE",
      fr: "fr-FR"
    }[locale];
    iatDate = decoded?.iat === undefined ? notAvailableText[locale] : new Date(Number(decoded.iat) * 1000).toLocaleString(currentLocaleForDate, dateStringFormat);
    t1 = decoded?.exp === undefined ? notAvailableText[locale] : new Date(Number(decoded.exp) * 1000).toLocaleString(currentLocaleForDate, dateStringFormat);
    $[0] = decoded;
    $[1] = locale;
    $[2] = iatDate;
    $[3] = t1;
  } else {
    iatDate = $[2];
    t1 = $[3];
  }
  const expDate = t1;
  if (!validPreviewToken) {
    const t2 = previewTokenInvalidText[locale];
    let t3;
    if ($[4] !== t2) {
      t3 = <div className="mx-0 my-8 border-t-[4px] border-t-red-800 bg-red-100 p-6 md:mx-12"><ParagraphText className="text-red-700">{t2}</ParagraphText></div>;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  const url = `/${locale}/${typeof slugs === "object" ? slugs.join("/") : slugs}`;
  const doesUrlMatch = decoded?.url === undefined || url === decoded.url;
  let t2;
  if ($[6] !== decoded || $[7] !== doesUrlMatch || $[8] !== expDate || $[9] !== iatDate || $[10] !== locale || $[11] !== previewTokenExpired) {
    t2 = decoded && <ul className="mt-4 list-disc pl-5">{decoded.url !== undefined && <li><strong>{previewForUrlText[locale]}</strong>{" "}{doesUrlMatch ? <>{decoded.url}</> : <span className="text-red-700">{decoded.url} ({urlDoesNotMatchText[locale]} {path})</span>}</li>}{decoded.id !== undefined && <li><strong>{documentIdText[locale]}</strong> {decoded.id}</li>}<li><strong>{issuedAtText[locale]}</strong> {iatDate}</li><li><strong>{expiresAtText[locale]}</strong>{" "}{previewTokenExpired ? <span className="text-red-700">{expDate}{expiredParenthesisText[locale]}</span> : expDate}</li></ul>;
    $[6] = decoded;
    $[7] = doesUrlMatch;
    $[8] = expDate;
    $[9] = iatDate;
    $[10] = locale;
    $[11] = previewTokenExpired;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t2) {
    t3 = <div className="mx-0 my-8 border-t-[4px] border-t-red-800 bg-red-100 p-6 md:mx-12">{t2}</div>;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
export const PreviewError: React.FC = () => {
  const $ = _c(21);
  const locale = useCurrentLocale(i18nConfig) as Locale;
  const searchParameters = useSearchParams();
  let t0;
  if ($[0] !== searchParameters) {
    const previewToken = searchParameters.get("preview-token") ?? "";
    t0 = decodeJwtPayload(previewToken);
    $[0] = searchParameters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const decoded = t0;
  const isValidPreviewToken = decoded !== undefined && typeof decoded === "object";
  const isPreviewTokenExpired = decoded?.exp !== undefined && new Date() > new Date(Number(decoded.exp) * 1000);
  const t1 = error403TitleText[locale];
  let t2;
  if ($[2] !== t1) {
    t2 = <HeadlineH1>{t1}</HeadlineH1>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = requestingPreviewModeText[locale];
  let t4;
  if ($[4] !== t3) {
    t4 = <TeaserText>{t3}</TeaserText>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = pageNotAvailableP1Text[locale];
  const t6 = homePageLinkText[locale];
  let t7;
  if ($[6] !== t6) {
    t7 = <LinkComponent onClick={backToHomeHandler} className="font-bold text-red-600" href="">{t6}</LinkComponent>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = pageNotAvailableP2Text[locale];
  let t9;
  if ($[8] !== t5 || $[9] !== t7 || $[10] !== t8) {
    t9 = <TeaserText>{t5}{" "}{t7}{t8}</TeaserText>;
    $[8] = t5;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== decoded || $[13] !== isPreviewTokenExpired || $[14] !== isValidPreviewToken) {
    t10 = <PreviewTokenAnalysis validPreviewToken={isValidPreviewToken} previewTokenExpired={isPreviewTokenExpired} decoded={decoded} />;
    $[12] = decoded;
    $[13] = isPreviewTokenExpired;
    $[14] = isValidPreviewToken;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t2 || $[18] !== t4 || $[19] !== t9) {
    t11 = <><main><article className="my-8 w-full max-w-2xl px-8 max-xl:mx-auto">{t2}{t4}{t9}{t10}</article></main></>;
    $[16] = t10;
    $[17] = t2;
    $[18] = t4;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
};